import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import trainingsAPI from "../../../services/trainings";
import slotsAPI from "../../../services/slots";
import classroomsAPI from "../../../services/classrooms";
import scheduleAPI from "../../../services/schedule";
import { Tooltip } from "primereact/tooltip";

const PrintScheduleAssignmentsPage = () => {
  const { schoolId, trainingId } = useParams();
  const { accessToken } = useAuthContext();
  const navigate = useNavigate();

  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, accessToken);
  const training = trainingQuery?.data?.data;

  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  const classroomsQuery = classroomsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  const queryList = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );

  // Sort slots by show_order if defined, otherwise by weekday (Monday first) and start_hour
  const sortedSlots = useMemo(() => {
    const slots = [...(slotsQuery?.data?.data || [])];
    return slots.sort((a, b) => {
      if (a.show_order !== b.show_order && (a.show_order !== undefined || b.show_order !== undefined)) {
        return (a.show_order || 0) - (b.show_order || 0);
      }
      const dayA = a.weekday === 0 ? 7 : a.weekday;
      const dayB = b.weekday === 0 ? 7 : b.weekday;
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      return (a.start_hour || "").localeCompare(b.start_hour || "");
    });
  }, [slotsQuery?.data?.data]);

  // Sort classrooms by show_order if defined, otherwise by name
  const sortedClassrooms = useMemo(() => {
    const classrooms = [...(classroomsQuery?.data?.data || [])];
    return classrooms.sort((a, b) => {
      if (a.show_order !== b.show_order && (a.show_order !== undefined || b.show_order !== undefined)) {
        return (a.show_order || 0) - (b.show_order || 0);
      }
      return (a.name || "").localeCompare(b.name || "", undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [classroomsQuery?.data?.data]);

  // Index schedule items by slot_id and classroom_id
  const scheduleMatrix = useMemo(() => {
    const scheduleData = queryList?.data?.data || [];
    const matrix = {};

    scheduleData.forEach((item) => {
      const slotId = item.slot_id || item.slot?.id;
      const classroomId = item.classroom_id || item.classroom?.id;
      if (slotId && classroomId) {
        const key = `${slotId}_${classroomId}`;
        if (!matrix[key]) {
          matrix[key] = [];
        }
        matrix[key].push(item);
      }
    });

    return matrix;
  }, [queryList?.data?.data]);

  const formatHour = (t) => {
    if (!t) return "";
    if (t.includes("h")) return t;
    const parts = t.split(":");
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parts[1];
      if (m === "00") return `${h}h`;
      return `${h}h${m}`;
    }
    return t;
  };

  const formatTimeRange = (start, end) => {
    if (!start && !end) return "";
    const s = formatHour(start);
    const e = formatHour(end);
    if (s && e) return `${s}-${e}`;
    return s || e;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    if (typeof dateString === "string" && dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("fr-FR");
      }
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("fr-FR");
  };

  const getBadgeStyle = (levelColor) => {
    if (levelColor && levelColor.trim() !== "") {
      return {
        backgroundColor: levelColor,
        color: "#000000",
      };
    }
    return {
      backgroundColor: "#ffffff",
      color: "#000000",
    };
  };

  const getTeacherName = (teacher) => {
    if (!teacher || !teacher.member) return "";
    if (teacher.member.name) return teacher.member.name;
    if (teacher.member.lastname && teacher.member.firstname) {
      return `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    return teacher.member.lastname || teacher.member.firstname || "";
  };

  const normalizeCapacity = (capacity) => {
    if (capacity === undefined || capacity === null || capacity === "") {
      return null;
    }
    const parsedCapacity = Number(capacity);
    return Number.isFinite(parsedCapacity) && parsedCapacity > 0
      ? parsedCapacity
      : null;
  };

  const getCapacityAlertInfo = (item, classroom) => {
    const classCapacity = normalizeCapacity(item?.classe?.max_capacity);
    const classroomCapacity = normalizeCapacity(
      classroom?.capacity ?? item?.classroom?.capacity
    );

    if (classCapacity === null || classroomCapacity === null) {
      return null;
    }

    if (classCapacity === classroomCapacity) {
      return null;
    }

    return {
      classCapacity,
      tooltip: `Capacité de la classe : ${classCapacity} - capacité de la salle : ${classroomCapacity}`,
    };
  };

  const isLoading =
    trainingQuery.isLoading ||
    slotsQuery.isLoading ||
    classroomsQuery.isLoading ||
    queryList.isLoading;

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Chargement...
      </div>
    );
  }

  return (
    <div className="print-page-wrapper">
      <Tooltip target=".print-capacity-tooltip" position="top" />
      <style>{`
        html, body {
          background: white !important;
          background-color: white !important;
        }

        .print-page-wrapper {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          background-color: white;
          min-height: 100vh;
        }

        .print-header-actions {
          position: fixed;
          top: 80px;
          right: 20px;
          display: flex;
          gap: 10px;
          z-index: 1000;
        }

        .back-button {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 10px 18px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background-color: #5a6268;
          transform: translateY(-1px);
        }

        .print-button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 10px 22px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .print-button:hover {
          background-color: #0056b3;
          transform: translateY(-1px);
        }

        .print-title-section {
          text-align: center;
          margin-bottom: 16px;
        }

        .print-main-title {
          font-size: 18px;
          font-weight: bold;
          color: #000000;
          margin: 0 0 4px 0;
        }

        .print-sub-title {
          font-size: 14px;
          font-weight: 600;
          color: #4b5563;
          margin: 0;
        }

        .assignments-print-table {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
          border: 2px solid #000000;
          background: #ffffff;
          font-family: Arial, sans-serif;
        }

        .assignments-print-table th,
        .assignments-print-table td {
          border: 1.5px solid #000000;
          text-align: center;
          vertical-align: middle;
          word-break: break-word;
        }

        .assignments-print-corner {
          background: #ffffff;
          padding: 8px 6px;
          font-weight: bold;
          font-size: 13px;
          color: #000000;
          width: 125px;
        }

        .assignments-print-classroom-th {
          background: #ffffff;
          padding: 6px 4px;
          color: #000000;
        }

        .assignments-print-room-name {
          font-weight: bold;
          font-size: 13px;
          color: #000000;
          margin-bottom: 2px;
          line-height: 1.15;
        }

        .assignments-print-room-cap {
          font-weight: normal;
          font-size: 11px;
          color: #6b7280;
          line-height: 1.15;
        }

        .assignments-print-slot-th {
          background: #ffffff;
          padding: 6px 6px;
          color: #000000;
          width: 125px;
        }

        .assignments-print-slot-name {
          font-weight: bold;
          font-size: 12.5px;
          line-height: 1.2;
          color: #000000;
          margin-bottom: 3px;
        }

        .assignments-print-slot-hours {
          font-weight: bold;
          font-size: 11.5px;
          color: #000000;
          line-height: 1.15;
        }

        .assignments-print-cell {
          padding: 4px 2px;
          background: #ffffff;
        }

        .print-cell-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 2px 2px;
        }

        .print-class-badge {
          display: inline-block;
          font-weight: bold;
          font-size: 11.5px;
          padding: 1px 5px;
          border-radius: 3px;
          text-align: center;
          max-width: 100%;
          word-break: break-word;
          line-height: 1.15;
        }

        .print-course-name {
          font-weight: normal;
          font-size: 10px;
          color: #374151;
          line-height: 1.15;
          text-align: center;
        }

        .print-class-capacity {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 9.5px;
          font-weight: 600;
          color: #374151;
          line-height: 1.15;
          text-align: center;
        }

        .print-class-capacity-warning {
          color: #f59e0b;
          font-size: 9px;
        }

        .print-teacher-name {
          font-weight: bold;
          font-size: 11.5px;
          color: #000000;
          line-height: 1.15;
          text-align: center;
        }

        .print-students-count {
          font-weight: bold;
          font-size: 12px;
          color: #000000;
          line-height: 1.15;
          text-align: center;
        }

        .print-footer {
          margin-top: 16px;
          text-align: center;
          font-size: 11px;
          color: #666666;
          line-height: 1.2;
        }

        .print-footer p {
          margin: 2px 0;
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 6mm 7mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            background-color: white !important;
            height: 100% !important;
            overflow: hidden !important;
          }

          .print-page-wrapper {
            padding: 0 !important;
            margin: 0 !important;
            min-height: auto !important;
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
          }

          .no-print {
            display: none !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-title-section {
            margin-bottom: 8px !important;
            flex-shrink: 0 !important;
          }

          .print-main-title {
            font-size: 16px !important;
            margin: 0 0 2px 0 !important;
            line-height: 1.2 !important;
          }

          .print-sub-title {
            font-size: 12.5px !important;
            margin: 0 !important;
            line-height: 1.2 !important;
          }

          .assignments-print-table {
            width: 100% !important;
            table-layout: fixed !important;
            border: 1.5px solid #000000 !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            break-inside: avoid !important;
            flex-grow: 1 !important;
          }

          .assignments-print-table th,
          .assignments-print-table td {
            border: 1px solid #000000 !important;
          }

          .assignments-print-corner {
            font-size: 12px !important;
            padding: 6px 3px !important;
            width: 120px !important;
          }

          .assignments-print-classroom-th {
            padding: 5px 3px !important;
          }

          .assignments-print-room-name {
            font-size: 12px !important;
            margin-bottom: 2px !important;
            line-height: 1.2 !important;
          }

          .assignments-print-room-cap {
            font-weight: normal !important;
            font-size: 10.5px !important;
            line-height: 1.2 !important;
          }

          .assignments-print-slot-th {
            width: 120px !important;
            padding: 6px 4px !important;
          }

          .assignments-print-slot-name {
            font-size: 12px !important;
            margin-bottom: 2px !important;
            line-height: 1.25 !important;
          }

          .assignments-print-slot-hours {
            font-size: 11px !important;
            line-height: 1.2 !important;
          }

          .assignments-print-cell {
            padding: 4px 2px !important;
          }

          .print-cell-item {
            gap: 2.5px !important;
            padding: 2px 1px !important;
          }

          .print-class-badge {
            font-size: 11px !important;
            padding: 1.5px 5px !important;
            line-height: 1.2 !important;
          }

          .print-course-name {
            font-size: 9.5px !important;
            line-height: 1.2 !important;
            margin: 1px 0 !important;
          }

          .print-class-capacity {
            font-size: 9px !important;
            gap: 2px !important;
            line-height: 1.2 !important;
          }

          .print-class-capacity-warning {
            font-size: 8.5px !important;
          }

          .print-teacher-name {
            font-size: 11px !important;
            line-height: 1.2 !important;
          }

          .print-students-count {
            font-size: 11.5px !important;
            line-height: 1.2 !important;
          }

          .print-footer {
            margin-top: 6px !important;
            font-size: 9.5px !important;
            line-height: 1.2 !important;
            flex-shrink: 0 !important;
          }

          .print-footer p {
            margin: 1px 0 !important;
          }
        }
      `}</style>

      {/* Floating Buttons */}
      <div className="no-print print-header-actions">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
        >
          <i className="pi pi-arrow-left" style={{ marginRight: "8px" }}></i>
          Retour
        </button>
        <button className="print-button" onClick={() => window.print()}>
          <i className="pi pi-print" style={{ marginRight: "8px" }}></i>
          Imprimer
        </button>
      </div>

      {/* Title & Subtitle */}
      <div className="print-title-section">
        <h1 className="print-main-title">
          Tableau récapitulatif des répartitions des classes
        </h1>
        {training?.name && (
          <h2 className="print-sub-title">{training.name}</h2>
        )}
      </div>

      {/* Matrix Table */}
      <table className="assignments-print-table">
        <thead>
          <tr>
            <th className="assignments-print-corner">Salle</th>
            {sortedClassrooms.map((classroom) => (
              <th key={classroom.id} className="assignments-print-classroom-th">
                <div className="assignments-print-room-name">{classroom.name}</div>
                {classroom.capacity > 0 && (
                  <div className="assignments-print-room-cap">
                    Effectif max {classroom.capacity}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedSlots.map((slot) => (
            <tr key={slot.id}>
              <th className="assignments-print-slot-th">
                <div className="assignments-print-slot-name">{slot.name}</div>
                <div className="assignments-print-slot-hours">
                  {formatTimeRange(slot.start_hour, slot.end_hour)}
                </div>
              </th>
              {sortedClassrooms.map((classroom) => {
                const key = `${slot.id}_${classroom.id}`;
                const items = scheduleMatrix[key] || [];

                return (
                  <td
                    key={`${slot.id}-${classroom.id}`}
                    className="assignments-print-cell"
                  >
                    {items.map((item) => {
                      const teacherName = getTeacherName(item.teacher);
                      const className = item.classe?.name || "";
                      const courseName = item.course?.name || "";
                      const levelColor =
                        item.classe?.level?.color || item.level?.color;
                      const capacityAlertInfo = getCapacityAlertInfo(
                        item,
                        classroom
                      );

                      return (
                        <div key={item.id} className="print-cell-item">
                          {className && (
                            <div
                              className="print-class-badge"
                              style={getBadgeStyle(levelColor)}
                            >
                              {className}
                            </div>
                          )}
                          {capacityAlertInfo && (
                            <div className="print-class-capacity">
                              <span>({capacityAlertInfo.classCapacity})</span>
                              <i
                                className="pi pi-exclamation-triangle print-class-capacity-warning print-capacity-tooltip"
                                data-pr-tooltip={capacityAlertInfo.tooltip}
                              ></i>
                            </div>
                          )}
                          {courseName && (
                            <div className="print-course-name">
                              {courseName}
                            </div>
                          )}
                          {teacherName && (
                            <div className="print-teacher-name">
                              {teacherName}
                            </div>
                          )}
                          {item.classe_total_students !== undefined &&
                            item.classe_total_students !== null && (
                              <div className="print-students-count">
                                {item.classe_total_students}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer */}
      <div className="print-footer">
        <p>
          Généré le : {new Date().toLocaleDateString("fr-FR")} à{" "}
          {new Date().toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
};

export default PrintScheduleAssignmentsPage;
